import { Button } from "@tql/ui-components-react/tql/buttons";
import { Skeleton } from "@tql/ui-components-react/tql/indicators";
import { bell_01, file_06 } from "@tql/ui-components-react/untitled/icons";
import type { UIEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import { formatDateToNumericUTC } from "~/shared/components/trax-grid/date-cell/date-cell.tsx";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import type { Load } from "~/shared/schemas/loads-schema";

import styles from "./mobile-shipment-board.module.css";

type MobileShipmentBoardProps = {
	detailsFlyoutEnabled: boolean;
	loading: boolean;
	onDetailsClick: (load: Load) => void;
	onDocumentClick: (load: Load) => void;
	onShipmentNotificationsClick: (load: Load) => void;
	shipmentData: Load[];
};

const PAGE_SIZE = 25;
const SCROLL_THRESHOLD = 2;
const LOADING_CARD_COUNT = 6;

const LoadingShipmentCard = () => (
	<div className={styles.shipmentCard} data-testid={"shipment-card-skeleton"}>
		<div className={styles.basicInfoRow}>
			<div className={styles.poCell}>
				<span className={styles.poLabel}>TQL PO:</span>{" "}
				<span className={styles.skeletonInlineText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
			</div>
			<div>
				<span className={styles.fieldLabel}>Mode:</span>{" "}
				<span className={styles.skeletonInlineText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
			</div>
			<span className={styles.skeletonBadge}>
				<Skeleton animation={{ type: "wave" }} shape={"rectangle"} />
			</span>
		</div>
		<div className={styles.locationDateRow}>
			<div className={styles.locationBlock}>
				<div className={styles.fieldLabel}>Pickup Location</div>
				<span className={styles.skeletonText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
				<span className={styles.skeletonText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
			</div>
			<div className={styles.dateBlock}>
				<div className={styles.fieldLabel}>Pickup Date</div>
				<span className={styles.skeletonInlineText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
			</div>
		</div>
		<div className={styles.locationDateRow}>
			<div className={styles.locationBlock}>
				<div className={styles.fieldLabel}>Delivery Location</div>
				<span className={styles.skeletonText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
				<span className={styles.skeletonText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
			</div>
			<div className={styles.dateBlock}>
				<div className={styles.fieldLabel}>Delivery Date</div>
				<span className={styles.skeletonInlineText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
			</div>
		</div>
		<div className={styles.lastLocationRow}>
			<div className={styles.fieldLabel}>Last Location</div>
			<span className={styles.skeletonText}>
				<Skeleton animation={{ type: "wave" }} shape={"text"} />
			</span>
		</div>
		<div className={styles.customerPoRow}>
			<span className={styles.fieldLabel}>Customer PO:</span>{" "}
			<span className={styles.skeletonInlineText}>
				<Skeleton animation={{ type: "wave" }} shape={"text"} />
			</span>
		</div>
		<hr className={styles.divider} />
		<div className={styles.actionsRow}>
			<span className={styles.skeletonAction}>
				<Skeleton animation={{ type: "wave" }} shape={"rectangle"} />
			</span>
			<span className={styles.skeletonAction}>
				<Skeleton animation={{ type: "wave" }} shape={"rectangle"} />
			</span>
		</div>
	</div>
);

export const MobileShipmentBoard = ({
	detailsFlyoutEnabled,
	loading,
	onDetailsClick,
	onDocumentClick,
	onShipmentNotificationsClick,
	shipmentData,
}: MobileShipmentBoardProps) => {
	const { isEnabled: notificationsEnabled } = useFeatureFlagGate(
		"traxLtlNotifications",
	);
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	useEffect(() => {
		setVisibleCount(PAGE_SIZE);
	}, [shipmentData]);

	const visibleShipments = useMemo(
		() => shipmentData.slice(0, Math.min(visibleCount, shipmentData.length)),
		[shipmentData, visibleCount],
	);

	const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
		if (visibleCount >= shipmentData.length) return;
		const target = event.currentTarget;
		const distanceFromBottom =
			target.scrollHeight - (target.scrollTop + target.clientHeight);
		if (distanceFromBottom <= SCROLL_THRESHOLD) {
			setVisibleCount((count) =>
				Math.min(count + PAGE_SIZE, shipmentData.length),
			);
		}
	};

	return (
		<div
			aria-busy={loading}
			className={styles.list}
			data-testid={"mobile-shipment-board-scroll-region"}
			onScroll={handleListScroll}
		>
			{loading
				? Array.from({ length: LOADING_CARD_COUNT }, (_, index) => (
						<LoadingShipmentCard key={`loading-${index}`} />
					))
				: visibleShipments.map((shipment) => {
						const notificationsMenuItemEnabled =
							notificationsEnabled && shipment.rateType === "LTL";

						return (
							<div key={shipment.poNumber} className={styles.shipmentCard}>
								<div className={styles.basicInfoRow}>
									<div className={styles.poCell}>
										<span className={styles.poLabel}>TQL PO:</span>{" "}
										{detailsFlyoutEnabled ? (
											<button
												className={styles.poLink}
												onClick={() => {
													onDetailsClick(shipment);
												}}
												type={"button"}
											>
												{shipment.poNumber}
											</button>
										) : (
											<span>{shipment.poNumber}</span>
										)}
									</div>
									<div>
										<span className={styles.fieldLabel}>Mode:</span>{" "}
										{shipment.rateType}
									</div>
									<div className={styles.statusBadge}>
										{shipment.formattedStatus}
									</div>
								</div>
								<div className={styles.locationDateRow}>
									<div className={styles.locationBlock}>
										<div className={styles.fieldLabel}>Pickup Location</div>
										<div className={styles.locationName}>
											{shipment.firstPickName}
										</div>
										<div>{shipment.formattedPickupAddress}</div>
									</div>
									<div className={styles.dateBlock}>
										<div className={styles.fieldLabel}>Pickup Date</div>
										<div>{formatDateToNumericUTC(shipment.loadDateValue)}</div>
									</div>
								</div>
								<div className={styles.locationDateRow}>
									<div className={styles.locationBlock}>
										<div className={styles.fieldLabel}>Delivery Location</div>
										<div className={styles.locationName}>
											{shipment.lastDropName}
										</div>
										<div>{shipment.formattedDeliveryAddress}</div>
									</div>
									<div className={styles.dateBlock}>
										<div className={styles.fieldLabel}>Delivery Date</div>
										<div>
											{formatDateToNumericUTC(shipment.deliveryDateValue)}
										</div>
									</div>
								</div>
								<div className={styles.lastLocationRow}>
									<div className={styles.fieldLabel}>Last Location</div>
									<div>{shipment.formattedLastLocation}</div>
								</div>
								<div className={styles.customerPoRow}>
									<span className={styles.fieldLabel}>Customer PO:</span>{" "}
									{shipment.customerPO}
								</div>
								<hr className={styles.divider} />
								<div className={styles.actionsRow}>
									{notificationsMenuItemEnabled ? (
										<Button
											aria-label={"Subscribe to Notifications"}
											onClick={() => onShipmentNotificationsClick(shipment)}
											svgIcon={bell_01}
											variant={"tertiary"}
										/>
									) : undefined}
									<Button
										aria-label={"View Documents"}
										onClick={() => onDocumentClick(shipment)}
										svgIcon={file_06}
										variant={"tertiary"}
									/>
								</div>
							</div>
						);
					})}
		</div>
	);
};
